import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { getLoanCollectionsService } from "../../../../services/loanService";
import useStore from "../../../../useStore";
import CustomerLoanDetails from "./CustomerLoanDetails";

function CustomerLoanCollection() {
  const { loanId } = useStore();
  const [loanCollections, setLoanCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expanded, setExpanded] = useState({
    loanDetails: true,
    loanCollections: true,
  });

  useEffect(() => {
    const fetchLoanCollections = async () => {
      try {
        setLoading(true);
        const data = await getLoanCollectionsService(
          loanId,
          page + 1,
          rowsPerPage
        );
        setLoanCollections(data.loanCollections);
        setTotalRecords(data.totalCollections);
        setLoading(false);
      } catch (error) {
        setError("Error fetching data");
        setLoading(false);
      }
    };

    fetchLoanCollections();
  }, [loanId, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (loanCollections.length < 1 || error) {
    return (
      <Typography color="error">
        {"No Loan Created For this Customer"}
      </Typography>
    );
  }

  return (
    <Box>
      {/* Accordion 1: Loan Details */}
      <Accordion
        expanded={expanded.loanDetails}
        onChange={handleAccordionChange("loanDetails")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Loan Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CustomerLoanDetails />
        </AccordionDetails>
      </Accordion>

      {/* Accordion 2: Loan Collections */}
      <Accordion
        expanded={expanded.loanCollections}
        onChange={handleAccordionChange("loanCollections")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Loan Collections</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper
            elevation={3}
            sx={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Loan Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Collection Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Interest Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Due Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Due By
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Penalty/Interest Collected Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Penalty/Interest Paid Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Penalty/Interest Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Collected Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Paid Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      EMI Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loanCollections.map((collection, index) => (
                    <TableRow
                      key={collection._id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "white",
                      }}
                    >
                      <TableCell>{collection.loan.loanCode}</TableCell>
                      <TableCell>{collection.collectionAmount}</TableCell>
                      <TableCell>{collection.interestAmount}</TableCell>
                      <TableCell>
                        {moment(collection.dueDate).format("DD-MM-YYYY")}
                      </TableCell>
                      <TableCell>{collection.dueBy}</TableCell>
                      <TableCell>
                        {collection.loan.frequency === "monthly"
                          ? collection?.interestCollectionDate
                            ? moment(collection.interestCollectionDate).format(
                                "DD-MM-YYYY"
                              )
                            : "-"
                          : collection?.penaltyCollectionDate
                          ? moment(collection.penaltyCollectionDate).format(
                              "DD-MM-YYYY"
                            )
                          : "-"}
                      </TableCell>

                      <TableCell>
                        {collection.loan.frequency === "monthly"
                          ? collection.paidInterestAmount || "-"
                          : collection.paidPenaltyAmount || "-"}
                      </TableCell>

                      {/* Penalty/Interest Status */}
                      <TableCell>
                        {collection.loan.frequency === "monthly" ? (
                          collection.isInterestPaid === true ? (
                            <Chip
                              label="Paid"
                              style={{
                                backgroundColor: "green",
                                color: "white",
                              }}
                            />
                          ) : (
                            <Chip
                              label="Unpaid"
                              style={{
                                backgroundColor: "red",
                                color: "white",
                              }}
                            />
                          )
                        ) : collection.isPenaltyPaid === true ? (
                          <Chip
                            label="Paid"
                            style={{
                              backgroundColor: "green",
                              color: "white",
                            }}
                          />
                        ) : (
                          <Chip
                            label="Unpaid"
                            style={{
                              backgroundColor: "red",
                              color: "white",
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        {collection?.collectionDate
                          ? moment(collection.collectionDate).format(
                              "DD-MM-YYYY"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>{collection.paidAmount}</TableCell>

                      <TableCell>
                        {collection.status === "paid" ? (
                          <>
                            <Chip
                              label="Paid"
                              style={{
                                backgroundColor: "green",
                                color: "white",
                              }}
                            />
                          </>
                        ) : collection.status === "partial" ? (
                          <>
                            <Chip
                              label="Partial Payment"
                              style={{
                                backgroundColor: "yellow",
                                color: "black",
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <Chip
                              label="Unpaid"
                              style={{
                                backgroundColor: "red",
                                color: "white",
                              }}
                            />
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                backgroundColor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
            />
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default CustomerLoanCollection;
